import EntityEditor from "../../../../../common/Tables/EntityEditor";
import { DropdownSelector, ValidatedDateInput, ValidatedDayOfWeekSelector, ValidatedNumberInput, ValidatedTextInput } from "../../../../../common/inputs/ValidatedInputs";
import { Box, MenuItem, Typography } from "@mui/material";
import { withQueryData } from "../../../../../common/withQueryData";
import CheckboxList from "../../../../../common/inputs/CheckboxList";
import { useRoomsData } from "../../../../../common/hooks/useRoomsData";
import { usePlansData } from "../../../../../common/hooks/useRatePlansData";
import { emptyRestriction, toRestrictionPayload } from "./restrictionDefaults";

const restrictionsEditorStyle = ({ theme }) => ({
    '& .MuiPaper-root': {
        maxWidth: '90%',
        width: 'max-content',
        minHeight: '500px',
    },
    '& .MuiDialogContent-root': {
        display: 'flex',
        gap: '50px',
    },
});

const sectionBorder = {
    '&::before': {
        content: '""',
        position: 'absolute',
        left: '-25px',
        width: '1px',
        height: '100%',
        backgroundColor: 'primary.light',
    },
};

const fmtDate = (date) => {
    if (!date) return null;
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const NameAndDatesSection = ({ restriction, onChange, disabled, entityChanged }) => (
    <Box sx={{
        display: 'flex',
        minWidth: '300px',
        flexDirection: 'column',
        position: 'relative',
    }}>
        <ValidatedTextInput
            size='small'
            margin="dense"
            id="name"
            label="Name"
            type="text"
            required
            autoFocus
            onChange={onChange}
            disabled={disabled}
            value={restriction.name || ''}
            disableError={!entityChanged}
            sx={{ width: '220px' }}
        />
        <ValidatedDateInput
            id='startDate'
            label="Start date"
            value={restriction.startDate}
            onChange={onChange}
            disabled={disabled}
            textFieldProps={{ size: 'small', sx: { width: '220px' } }}
            disableError={!entityChanged}
        />
        <ValidatedDateInput
            id='endDate'
            label="End date"
            value={restriction.endDate}
            onChange={onChange}
            disabled={disabled}
            textFieldProps={{ size: 'small', sx: { width: '220px' } }}
            disableError={!entityChanged}
            minDate={restriction.startDate}
            defaultCalendarMonth={restriction.startDate}
        />
    </Box>
);

// ─── Section: availability logic ────────────────────────────────────────────

const AvailabilityLogicSection = ({
    restriction,
    onChange,
    disabled,
    entityChanged,
    planDisabled,
    ratePlan,
    availableRooms,
}) => {
    const onChangePlanDisabled = (checked) => {
        onChange('planDisabled', checked, true);
    };

    const { startDate, endDate } = restriction;

    let checkinPeriodPhrase;
    if (!startDate && !endDate) {
        checkinPeriodPhrase = 'any date';
    } else if (startDate && endDate) {
        checkinPeriodPhrase = `between ${fmtDate(startDate)} and ${fmtDate(endDate)}`;
    } else if (startDate) {
        checkinPeriodPhrase = `on or after ${fmtDate(startDate)}`;
    } else {
        checkinPeriodPhrase = `on or before ${fmtDate(endDate)}`;
    }

    return (
        <Box sx={{
            display: 'flex',
            minWidth: '500px',
            minHeight: '550px',
            flexDirection: 'column',
            position: 'relative',
            gap: '16px',
            ...sectionBorder,
        }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <Typography>When check-in is</Typography>
                <Typography><strong>{checkinPeriodPhrase}</strong></Typography>
                <Typography>the rate plan should be</Typography>
                <DropdownSelector
                    sx={{
                        width: '200px'
                    }}
                    size='small'
                    id="disabledFlag"
                    onChange={(value) => onChangePlanDisabled(value)}
                    disabled={disabled}
                    value={restriction.planDisabled}
                >
                    <MenuItem key={"true"} value={true}>{"disabled"}</MenuItem>
                    <MenuItem key={"false"} value={false}>{"available only when"}</MenuItem>
                </DropdownSelector>
                <Box sx={{ display: restriction.planDisabled ? 'none' : 'flex', flexDirection: 'column', gap: '10px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'baseline' }}>
                        <Typography>length of stay is</Typography>
                        <DropdownSelector
                            sx={{
                                width: '180px'
                            }}
                            size='small'
                            id="losType"
                            onChange={(value) => onChange('multiplyMinLos', value, true)}
                            disabled={disabled}
                            value={restriction.multiplyMinLos}
                        >
                            <MenuItem key={"true"} value={true}>{"multiplication of"}</MenuItem>
                            <MenuItem key={"false"} value={false}>{"minimum"}</MenuItem>
                        </DropdownSelector>
                        <ValidatedNumberInput
                            size='small'
                            margin="dense"
                            id="minLos"
                            min={1}
                            required
                            decimals={0}
                            onChange={onChange}
                            value={restriction.minLos}
                            disableError={!entityChanged}
                            sx={{ width: 50 }}
                            hideButtons={true}
                        />
                        <Typography>nights</Typography>
                    </Box>
                    <Typography>and check-in is on:</Typography>
                    <ValidatedDayOfWeekSelector
                        id='allowedCheckinDaysOfWeek'
                        value={restriction.allowedCheckinDaysOfWeek}
                        sx={{ paddingTop: '10px' }}
                        onChange={onChange}
                        required
                    />
                    <Typography>and check-out is on:</Typography>
                    <ValidatedDayOfWeekSelector
                        id='allowedCheckoutDaysOfWeek'
                        value={restriction.allowedCheckoutDaysOfWeek}
                        sx={{ paddingTop: '10px' }}
                        onChange={onChange}
                        required
                    />
                </Box>
            </Box>
        </Box>
    );
};

// ─── Section: selected rooms ──────────────────────────────────────────────────

const SelectedRoomsSection = ({ restriction, onChange, availableRooms }) => (
    <Box sx={{
        display: 'flex',
        minWidth: '300px',
        flexDirection: 'column',
        position: 'relative',
        ...sectionBorder,
    }}>
        <CheckboxList
            id='selectedRooms'
            label='Selected rooms'
            selectedItems={restriction.selectedRooms}
            onChange={onChange}
            availableItems={availableRooms}
            required
        />
    </Box>
);

// ─── Editor content ───────────────────────────────────────────────────────────

const RestrictionEditorContent = ({ data: availableRooms, ratePlan, entity, onChange, disabled, entityChanged }) => {
    const restriction = entity;
    const planDisabled = !!restriction.planDisabled;

    return (
        <>
            <NameAndDatesSection
                restriction={restriction}
                onChange={onChange}
                disabled={disabled}
                entityChanged={entityChanged}
            />
            <AvailabilityLogicSection
                startDate={restriction.startDate}
                endDate={restriction.endDate}
                restriction={restriction}
                onChange={onChange}
                disabled={disabled}
                entityChanged={entityChanged}
                planDisabled={planDisabled}
                ratePlan={ratePlan}
                availableRooms={availableRooms}
            />
            <SelectedRoomsSection
                restriction={restriction}
                onChange={onChange}
                availableRooms={availableRooms}
            />
        </>
    );
};

const EditorContentWithQueryData = withQueryData(RestrictionEditorContent);

// ─── Dialog ─────────────────────────────────────────────────────────────────────

const RestrictionEditor = ({ ratePlan, propertyId, onSuccess, onClose, editedEntity, onError }) => {
    // eslint-disable-next-line no-unused-vars
    const { queryResults } = useRoomsData(propertyId);
    const { data: availableRooms, error, isLoading, isError, isFetching, refetch } = queryResults;//eslint-disable-line no-unused-vars

    const { createRestriction, updateRestriction } = usePlansData(propertyId);

    let defaultRestriction = editedEntity || { ...emptyRestriction, selectedRooms: availableRooms ? availableRooms : [] };
    const isEditing = !!editedEntity;

    const create = async (restriction) => {
        return createRestriction(ratePlan.id, toRestrictionPayload(restriction));
    }

    const update = async (restriction) => {
        return updateRestriction(ratePlan.id, restriction.id, toRestrictionPayload(restriction));
    }

    const planLabel = ratePlan?.name ? ` (${ratePlan.name})` : '';
    const restrictionLabel = editedEntity?.name ? `: ${editedEntity.name}` : '';
    const title = isEditing
        ? `Editing restriction${restrictionLabel}${planLabel}`
        : `New restriction${planLabel}`;

    return <EntityEditor
        dialogProps={{ sx: restrictionsEditorStyle }}
        title={title}
        defaultEntity={defaultRestriction}
        entityName='Restriction'
        editing={isEditing}
        onClose={onClose}
        onSuccess={onSuccess}
        onError={onError}
        EditorContent={(props) => <EditorContentWithQueryData {...props} ratePlan={ratePlan} queryData={queryResults} />}
        submitAction={isEditing ? update : create}
        hideSubmitButton={isLoading || isFetching || isError}
    />
}

export default RestrictionEditor;
